import { useCallback, useRef, useState } from "react";
import {
    draggable,
    dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { pointerOutsideOfPreview } from "@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview";
import { ILibraryFolder } from "@/api";
import { useQueryClient } from "@tanstack/react-query";
import { executeMoveWithUndo } from "../utils/moveWithUndo";
import { isValidDropForTarget } from "../utils/dropValidation";
import { ILibraryDragItem } from "@/components/shared/document-picker/DocumentPicker.types";

export type { ILibraryDragItem };

interface DragState {
    isDragging: boolean;
    draggedItems: ILibraryDragItem[];
    hoveredDropTargetId: string | null;
}

interface UseLibraryDragAndDropOptions {
    folders: ILibraryFolder[];
    getSelectedItems: () => ILibraryDragItem[];
    isItemSelected: (id: string) => boolean;
    setSelection: (ids: Set<string>) => void;
    onMoveComplete: () => void;
    /** The client workspace this surface is mounted in, when any: rides
     * executeMoveWithUndo's options so the move AND its undo invalidate
     * ["client", clientId] (the workspace header). The undo leg has no
     * onComplete chain — without this the header goes stale after an
     * undo (insurescope-g5bqp: the engine supported it, no caller fed
     * it). Absent outside a workspace. */
    clientId?: number;
}

export function useLibraryDragAndDrop({
    folders,
    getSelectedItems,
    isItemSelected,
    setSelection,
    onMoveComplete,
    clientId,
}: UseLibraryDragAndDropOptions) {
    const queryClient = useQueryClient();

    const [dragState, setDragState] = useState<DragState>({
        isDragging: false,
        draggedItems: [],
        hoveredDropTargetId: null,
    });

    const dragStateRef = useRef(dragState);
    dragStateRef.current = dragState;

    const executeDrop = useCallback(
        async (
            items: ILibraryDragItem[],
            targetFolderId: number | null,
            targetFolderName: string
        ) => {
            await executeMoveWithUndo(
                items,
                targetFolderId,
                targetFolderName,
                queryClient,
                onMoveComplete,
                { clientId }
            );
        },
        [queryClient, onMoveComplete, clientId]
    );

    const isValidDropTarget = useCallback(
        (
            items: ILibraryDragItem[],
            targetFolderNumericId: number | null,
            targetIsForeignShared = false
        ): boolean => {
            return isValidDropForTarget(
                folders,
                items,
                targetFolderNumericId,
                targetIsForeignShared
            );
        },
        [folders]
    );

    const attachDraggable = useCallback(
        (
            element: HTMLElement,
            item: ILibraryDragItem,
            onGeneratePreview: (container: HTMLElement, count: number) => void
        ) => {
            return draggable({
                element,
                getInitialData: () => {
                    const selected = getSelectedItems();
                    const isInSelection = isItemSelected(item.id);
                    const allItems = selected.some((s) => s.id === item.id)
                        ? selected
                        : [item];
                    return { item, allItems, isInSelection };
                },
                onGenerateDragPreview: ({ source, nativeSetDragImage }) => {
                    const allItems = source.data.allItems as ILibraryDragItem[];
                    setCustomNativeDragPreview({
                        nativeSetDragImage,
                        getOffset: pointerOutsideOfPreview({
                            x: "16px",
                            y: "8px",
                        }),
                        render: ({ container }) => {
                            onGeneratePreview(container, allItems?.length ?? 1);
                        },
                    });
                },
                onDragStart: ({ source }) => {
                    const items = source.data.allItems as ILibraryDragItem[];

                    // If the dragged item wasn't part of the selection,
                    // getInitialData returned [item] — update selection to match
                    if (
                        !source.data.isInSelection &&
                        items.length === 1 &&
                        items[0].id === item.id
                    ) {
                        setSelection(new Set([item.id]));
                    }

                    setDragState({
                        isDragging: true,
                        draggedItems: items,
                        hoveredDropTargetId: null,
                    });
                },
                onDrop: ({ location }) => {
                    if (location.current.dropTargets.length > 0) {
                        setSelection(new Set());
                    }
                    setDragState({
                        isDragging: false,
                        draggedItems: [],
                        hoveredDropTargetId: null,
                    });
                },
            });
        },
        [getSelectedItems, isItemSelected, setSelection]
    );

    const attachDropTarget = useCallback(
        (
            element: HTMLElement,
            folderRowId: string,
            folderNumericId: number,
            folderName: string,
            isForeignShared: boolean
        ) => {
            return dropTargetForElements({
                element,
                getData: () => ({
                    folderId: folderNumericId,
                    folderName,
                    folderRowId,
                    isForeignShared,
                }),
                canDrop: ({ source }) => {
                    const sourceItem = source.data.item as ILibraryDragItem;
                    if (!sourceItem) return false;
                    const items =
                        (source.data.allItems as ILibraryDragItem[]) ??
                        (dragStateRef.current.draggedItems.length > 0
                            ? dragStateRef.current.draggedItems
                            : [sourceItem]);
                    return isValidDropTarget(
                        items,
                        folderNumericId,
                        isForeignShared
                    );
                },
                onDragEnter: () => {
                    setDragState((prev) => ({
                        ...prev,
                        hoveredDropTargetId: folderRowId,
                    }));
                },
                onDragLeave: () => {
                    setDragState((prev) => ({
                        ...prev,
                        hoveredDropTargetId:
                            prev.hoveredDropTargetId === folderRowId
                                ? null
                                : prev.hoveredDropTargetId,
                    }));
                },
                onDrop: ({ source }) => {
                    const sourceItem = source.data.item as ILibraryDragItem;
                    const items =
                        (source.data.allItems as ILibraryDragItem[]) ??
                        (dragStateRef.current.draggedItems.length > 0
                            ? dragStateRef.current.draggedItems
                            : [sourceItem]);
                    executeDrop(items, folderNumericId, folderName);
                },
            });
        },
        [isValidDropTarget, executeDrop]
    );

    return {
        dragState,
        attachDraggable,
        attachDropTarget,
        isValidDropTarget,
    };
}
